'use client';
import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogBackdrop, DialogTitle } from '@headlessui/react';
import useResponsive from '../hooks/use-responsive/use-responsive';
import { formatBytes, getImagesToCache, getDataUrlsToCache, GTMEvent } from '@moar/utils';
import { useContextData } from '../context/data';
import { useIsOnline } from '../context/online';
import usePwaSupported from '../hooks/use-pwa-supported/use-pwa-supported';
import usePersistedState from '../hooks/use-persisted-state/use-persisted-state';
import { IconCloseBox } from '@moar/svg';
import useInterval from '../hooks/use-interval/use-interval';
import { postToServiceWorker } from '../sw-message';

declare const navigator: any;

/* eslint-disable-next-line */
export interface ModalImagesCacheProps {}

// used to be just for safari, but now for all since install isn't working automatically

export function ModalImagesCache(props: ModalImagesCacheProps) {
    const { isSupportedSafari, isSupportedDesktop, isSupportedAndroid } = usePwaSupported();
    const { isMobileDesign } = useResponsive();
    const isOnline = useIsOnline();
    const data = useContextData();
    const [status, setStatus] = useState<'loading' | 'success' | null>(null);
    const [cacheImages, setCacheImages] = useState<null | number>(null);
    const [cacheImageSize, setCacheImageSize] = useState<null | number>(null);
    const [cacheProgress, setCacheProgress] = useState<{ processed: number; total: number } | null>(null);
    const [pagesProgress, setPagesProgress] = useState<{ processed: number; total: number } | null>(null);
    const [cachePagesDone, setCachePagesDone] = useState<boolean>(false);
    const [cacheImagesDone, setCacheImagesDone] = useState<boolean>(false);
    const [modalShown, setModalShown] = usePersistedState('safari-modal-shown', false);
    const [displayMode, setDisplayMode] = useState<string | null>(null);
    const isStandaloneRef = useRef<boolean>(false);
    const [isReady, setIsReady] = useState<boolean>(false);
    const [showReadyError, setReadyError] = useState<boolean>(false);

    useEffect(() => {
        let displayMode = 'browser';
        // hacking this
        const navigator = window.navigator as any;
        if (navigator['standalone'] as any) {
            displayMode = 'standalone';
        }
        if (window.matchMedia('(display-mode: standalone)').matches) {
            displayMode = 'standalone';
        }
        setDisplayMode(displayMode);

        const standalone = window.matchMedia('(display-mode: standalone)');
        standalone.onchange = (e) => {
            if (e.matches && isStandaloneRef.current === false) {
                setDisplayMode('standalone');
                isStandaloneRef.current = true;
            }
        };
    }, []);

    // https://usehooks-ts.com/react-hook/use-interval
    // In safari, poll to see precache files are loaded
    // listener only fires after after all that is done
    useInterval(
        () => {
            postToServiceWorker({ command: 'log', message: 'ready ping', action: 'READY_PING' });
        },
        // Delay in milliseconds or null to stop it
        !isReady ? 2000 : null
    );

    // Listen for the SW's broadcast answers. Attach to navigator.serviceWorker (always
    // present) rather than window.serwist, so the listener can't silently fail to attach
    // when serwist initializes after this component mounts (a cause of the stuck loader).
    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
        const handleMessage = (event: any) => {
            if (event.data?.type === 'READY_PING_ANSWER') {
                setIsReady(true);
            }
            if (event.data?.type === 'CACHE_PAGES_PROGRESS') {
                setPagesProgress({ processed: event.data.payload.processed, total: event.data.payload.total });
            }
            if (event.data?.type === 'CACHE_PAGES_DONE') {
                setCachePagesDone(true);
            }
            if (event.data?.type === 'CACHE_IMAGES_PROGRESS') {
                setCacheProgress({ processed: event.data.payload.processed, total: event.data.payload.total });
            }
            if (event.data?.type === 'CACHE_IMAGES_DONE') {
                setCacheImages(event.data.payload.count);
                setCacheImagesDone(true);
            }
        };
        navigator.serviceWorker.addEventListener('message', handleMessage);
        return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
    }, []);

    // Reliable readiness signal: navigator.serviceWorker.ready resolves once the SW is
    // active — which, with serwist, only happens after the precache install succeeds. This
    // covers the common case where the READY_PING round-trip never completes because the SW
    // isn't controlling the page yet on first load.
    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
            setIsReady(true); // no SW support → never trap the user on "Loading App…"
            return;
        }
        let cancelled = false;
        navigator.serviceWorker.ready
            .then(() => {
                if (!cancelled) setIsReady(true);
            })
            .catch(() => {
                /* the hard timeout below is the final fallback */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Never leave the user stuck: warn at 5s, and proceed anyway at 10s so the app stays
    // usable even if every readiness signal above failed (the SW keeps caching in the bg).
    useEffect(() => {
        const warnTimeout = setTimeout(() => setReadyError(true), 15000);
        const proceedTimeout = setTimeout(() => setIsReady(true), 30000);
        return () => {
            clearTimeout(warnTimeout);
            clearTimeout(proceedTimeout);
        };
    }, []);

    // Both the page-data warm and the image cache report DONE separately; the flow is
    // complete only once both have finished.
    useEffect(() => {
        if (status === 'loading' && cachePagesDone && cacheImagesDone) {
            setStatus('success');
        }
    }, [status, cachePagesDone, cacheImagesDone]);

    // Cache watchdog: if caching stops reporting progress for 60s (and no DONE arrives),
    // surface the Close button instead of trapping the user on the spinner. The SW keeps
    // caching in the background. Timer resets on each progress update (pages or images).
    useEffect(() => {
        if (status !== 'loading') return;
        const stall = setTimeout(() => setStatus('success'), 60000);
        return () => clearTimeout(stall);
    }, [status, cacheProgress, pagesProgress]);

    const handleCacheImages = () => {
        if (!data || typeof navigator === 'undefined' || !navigator.serviceWorker) return;
        setStatus('loading');
        setCacheImages(null);
        setCacheProgress(null);
        setPagesProgress(null);
        setCachePagesDone(false);
        setCacheImagesDone(false);

        const imageUrls = getImagesToCache({ data, isMobile: isMobileDesign });

        // Warm the page data (/_next/data/*.json) so object/section pages work offline even
        // if serwist's precache install dropped a few entries. buildId is exposed by Next's
        // Pages Router on window.__NEXT_DATA__.
        const buildId = (window as any).__NEXT_DATA__?.buildId as string | undefined;
        const dataUrls = buildId ? getDataUrlsToCache({ data, buildId }) : [];

        if (dataUrls.length > 0) {
            postToServiceWorker({ command: 'log', message: 'cache pages', action: 'CACHE_PAGES', payload: dataUrls });
        } else {
            // Nothing to warm (no buildId) — don't block completion on it.
            setCachePagesDone(true);
        }

        postToServiceWorker({ command: 'log', message: 'cache images', action: 'CACHE_IMAGES', payload: imageUrls });
    };

    const handleClose = () => {
        GTMEvent({ eventName: 'app_install' });
        setModalShown(true);
    };

    console.log({
        modalShown,
        isSupportedSafari,
        isSupportedDesktop,
        isSupportedAndroid,
        displayMode,
    });

    // if modal is shown on safari desktop, images will install, but will be lost in standalone and they will have to be redownloaded
    // display mode is
    const showModal =
        !modalShown && displayMode === 'standalone' && (isSupportedSafari || isSupportedDesktop || isSupportedAndroid);

    if (!showModal) return null;

    return (
        <Dialog open={!modalShown} onClose={() => handleClose()} className="fixed inset-0 z-[100]">
            {/* The backdrop, rendered as a fixed sibling to the panel container */}
            <DialogBackdrop className="fixed inset-0 bg-black opacity-75" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <div className="relative w-full px-6 py-6 overflow-y-scroll min-h-[100px] md:max-w-md bg-ivory">
                    {showReadyError && !isReady && (
                        <>
                            <div className="absolute z-50 right-3 top-3">
                                <button onClick={() => handleClose()}>
                                    <IconCloseBox />
                                    <span className="sr-only">Close Modal</span>
                                </button>
                            </div>
                            <DialogTitle className="font-serif text-2xl transition-all md:text-4xl">
                                This is taking longer than it should
                            </DialogTitle>
                            <div className="flex flex-col items-center justify-center">
                                <p className="mt-2 text-sm text-center">
                                    We recommend you close the app and try again. If you don't, the app might not work
                                    properly offline. If this persists, please contact us.
                                </p>
                            </div>
                        </>
                    )}
                    {!isReady && (
                        <div className="flex flex-col items-center justify-center">
                            <div className="font-serif font-bold animate-pulse text-base-plus">Loading App ...</div>
                            <p className="mt-2 text-sm text-center">
                                This may take a while depending on your connection speed. This only happens the first
                                time the app is opened.
                            </p>
                            {/* <p>DEBUG:</p>
                            <ul>
                                <li>modalShown: {modalShown}</li>
                                <li>displayMode: {displayMode}</li>
                                <li>isSupportedSafari: {isSupportedSafari}</li>
                                <li>isSupportedDesktop: {isSupportedDesktop}</li>
                                <li>isSupportedAndroid: {isSupportedAndroid}</li>
                            </ul> */}
                        </div>
                    )}
                    {isReady && (
                        <>
                            <div className="absolute z-50 right-3 top-3">
                                <button onClick={() => handleClose()}>
                                    <IconCloseBox />
                                    <span className="sr-only">Close Modal</span>
                                </button>
                            </div>
                            <DialogTitle className="font-serif text-2xl transition-all md:text-4xl">
                                Option to Load Images
                            </DialogTitle>

                            {isOnline && (
                                <div className="flex items-center">
                                    <div className="mt-4">
                                        <div>
                                            <h2 className="font-sans text-base">
                                                Would you like to load the images so you can use the app offline?
                                            </h2>
                                            <p className="my-2 text-sm">
                                                We recommend connecting to wifi instead of using cellular data before
                                                loading the images.
                                            </p>
                                            {status === null && (
                                                <div className="flex items-center justify-between mt-4 space-x-2">
                                                    <button
                                                        className="btn btn--primary"
                                                        onClick={() => handleCacheImages()}
                                                    >
                                                        Load images
                                                    </button>
                                                    <button
                                                        className="btn btn--outline-dark"
                                                        onClick={() => handleClose()}
                                                    >
                                                        No, Thanks
                                                    </button>
                                                </div>
                                            )}
                                            {status === 'loading' && (
                                                <div className="my-4 font-serif font-bold animate-pulse">
                                                    {!cachePagesDone && pagesProgress ? (
                                                        <>Preparing pages {pagesProgress.processed}/{pagesProgress.total}...</>
                                                    ) : (
                                                        <>
                                                            Loading images
                                                            {cacheProgress ? ` ${cacheProgress.processed}/${cacheProgress.total}` : ''}{' '}
                                                            (this may take a while)...
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            {status === 'success' && (
                                                <div className="my-4 font-bold">
                                                    <div className="mb-4">
                                                        Done! Loaded {cacheImages && cacheImages} images{' '}
                                                        {/* {cacheImageSize !== null ? `${formatBytes(cacheImageSize)}` : ''} */}
                                                    </div>
                                                    <button
                                                        className="btn btn--outline-dark"
                                                        onClick={() => handleClose()}
                                                    >
                                                        Close
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!isOnline && (
                                <div>
                                    <p className="mt-2">You must be online to load images for offline use.</p>
                                    <button className="btn btn--outline-dark" onClick={() => handleClose()}>
                                        Okay
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Dialog>
    );
}

export default ModalImagesCache;
